import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { getQueryString } from "../../common/utils"
import {
  PENDING_SUBSCRIPTIONS_ENDPOINT,
  RTK_SLICE_API_LIST
} from "../../common/constants"
import { baseQuery, getTags } from "../api"
import bbApi from "../../api"
import {
  APIError,
  PendingSubscriptionFull,
  PendingSubscriptionsGetResponse,
  PendingSubscriptionEvent,
  PendingSubscriptionEventsGetResponse,
  CreatePendingSubscriptionPayload,
  UpdatePendingSubscriptionPayload,
  ForcePriceResponse
} from "../../types"

const reducerPath = "pendingSubscriptions"
const pendingSubscriptionDetails = "pending-subscription-details"

export const pendingSubscriptionsApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath, pendingSubscriptionDetails],
  endpoints: builder => ({
    fetchPendingSubscriptions: builder.query<
      PendingSubscriptionFull[],
      Record<string, any>
    >({
      query: (params: Record<string, any>) =>
        `${PENDING_SUBSCRIPTIONS_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: PendingSubscriptionsGetResponse) =>
        responseData?.results ?? [],
      providesTags: result => getTags(reducerPath, "id", result)
    }),

    fetchPendingSubscriptionById: builder.query<
      PendingSubscriptionFull | undefined,
      { id: number }
    >({
      query: ({ id }) => `${PENDING_SUBSCRIPTIONS_ENDPOINT}?id=${id}`,
      transformResponse: (responseData: PendingSubscriptionsGetResponse) =>
        responseData?.results?.[0],
      providesTags: result =>
        getTags(pendingSubscriptionDetails, "id", result ? [result] : [])
    }),

    fetchPendingSubscriptionEvents: builder.query<
      PendingSubscriptionEvent[],
      { pending_id: number; event_type?: "updated" | "priced" }
    >({
      queryFn: async ({ pending_id, event_type }) => {
        const response = await bbApi.pendingSubscriptionEvents.get(
          pending_id,
          event_type
        )

        const results = (response as PendingSubscriptionEventsGetResponse)
          ?.results
        return { data: results ?? [] }
      }
    }),

    createPendingSubscription: builder.mutation<
      any | APIError,
      CreatePendingSubscriptionPayload
    >({
      queryFn: async (payload: CreatePendingSubscriptionPayload) => {
        const response = await bbApi.pendingSubscriptions.create(payload)
        return { data: response }
      },
      invalidatesTags: [{ type: reducerPath as any, id: RTK_SLICE_API_LIST }]
    }),

    updatePendingSubscription: builder.mutation<
      any | APIError,
      UpdatePendingSubscriptionPayload
    >({
      queryFn: async (payload: UpdatePendingSubscriptionPayload) => {
        const response = await bbApi.pendingSubscriptions.update(payload)
        return { data: response }
      },
      invalidatesTags: (result, error, payload) => [
        { type: pendingSubscriptionDetails, id: payload.id.toString() },
        { type: pendingSubscriptionDetails, id: RTK_SLICE_API_LIST },
        { type: reducerPath as any, id: RTK_SLICE_API_LIST }
      ]
    }),

    forcePricePendingSubscription: builder.mutation<
      ForcePriceResponse | APIError,
      { id: number }
    >({
      queryFn: async ({ id }) => {
        const response = await bbApi.pendingSubscriptions.forcePrice(id)
        return { data: response }
      },
      invalidatesTags: (result, error, payload) => [
        { type: pendingSubscriptionDetails, id: payload.id.toString() },
        { type: pendingSubscriptionDetails, id: RTK_SLICE_API_LIST }
      ]
    }),

    deletePendingSubscription: builder.mutation<any | APIError, { id: number }>(
      {
        queryFn: async ({ id }) => {
          const response = await bbApi.pendingSubscriptions.delete(id)
          return { data: response }
        },
        invalidatesTags: (result, error, payload) => [
          { type: pendingSubscriptionDetails, id: payload.id.toString() },
          { type: pendingSubscriptionDetails, id: RTK_SLICE_API_LIST },
          { type: reducerPath as any, id: RTK_SLICE_API_LIST }
        ]
      }
    )
  })
})

export const {
  useFetchPendingSubscriptionsQuery,
  useFetchPendingSubscriptionByIdQuery,
  useLazyFetchPendingSubscriptionByIdQuery,
  useFetchPendingSubscriptionEventsQuery,
  useCreatePendingSubscriptionMutation,
  useUpdatePendingSubscriptionMutation,
  useForcePricePendingSubscriptionMutation,
  useDeletePendingSubscriptionMutation
} = pendingSubscriptionsApi
